import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, KeyRound, ShieldCheck, RefreshCw, Copy, Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CollectionsManager from "@/components/CollectionsManager";
import SiteSettingsManager from "@/components/SiteSettingsManager";
import StockAlertsManager from "@/components/StockAlertsManager";

const EMPTY = {
  name: "", category: "sarees", price: 0, compare_at_price: null,
  description: "", images: [""], sizes: ["Free Size"], tags: [], stock: 0,
  featured_in_category: false, featured_on_homepage: false,
};

function rupee(n) { return `₹${Number(n).toLocaleString("en-IN")}`; }

export default function Admin() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [revealedKey, setRevealedKey] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    setUploading(true);
    const newUrls = [];
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`"${file.name}" is larger than 5 MB. Please compress and retry.`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(data.url);
      } catch (err) {
        toast.error(`Upload failed for ${file.name}`);
      }
    }
    if (newUrls.length) {
      setForm((f) => ({ ...f, images: [...f.images.filter(Boolean), ...newUrls] }));
      toast.success(`${newUrls.length} image${newUrls.length > 1 ? "s" : ""} uploaded`);
    }
    setUploading(false);
  };

  const removeImage = (url) => {
    setForm((f) => ({ ...f, images: f.images.filter((u) => u !== url) }));
  };

  // Memoise the cleaned image list — avoids running .filter() on every render.
  const visibleImages = useMemo(
    () => (form.images || []).filter(Boolean),
    [form.images],
  );

  const load = async () => {
    const [{ data: p }, { data: o }, { data: u }] = await Promise.all([
      api.get("/products", { params: { limit: 1000 } }),
      api.get("/orders").catch(() => ({ data: [] })),
      api.get("/admin/users").catch(() => ({ data: [] })),
    ]);
    setProducts(p); setOrders(o); setUsers(u);
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (!user) return <div className="px-6 md:px-24 py-24 text-center"><div className="font-serif-h text-3xl">Admin login required</div><Link to="/login" className="btn-ink mt-6 inline-flex">Sign In</Link></div>;
  if (user.role !== "admin") return <div className="px-6 md:px-24 py-24 text-center font-serif-h text-3xl">Not authorised</div>;

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, compare_at_price: p.compare_at_price ?? null }); setOpen(true); };

  const save = async () => {
    // Client-side validation with clear error messages.
    const name = (form.name || "").trim();
    const description = (form.description || "").trim();
    const images = (form.images || []).map((s) => s.trim()).filter(Boolean);
    if (!name) { toast.error("Name is required"); return; }
    if (!description) { toast.error("Description is required"); return; }
    if (images.length === 0) { toast.error("Please add at least one image URL"); return; }

    const payload = {
      ...form,
      name,
      description,
      price: Number(form.price) || 0,
      compare_at_price: form.compare_at_price ? Number(form.compare_at_price) : null,
      stock: Number(form.stock) || 0,
      images,
      sizes: (form.sizes || []).map((s) => s.trim()).filter(Boolean),
      tags: Array.isArray(form.tags) ? form.tags : [],
      featured_in_category: !!form.featured_in_category,
      featured_on_homepage: !!form.featured_on_homepage,
    };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setOpen(false); load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => `${d.loc?.slice(-1)?.[0] || "field"}: ${d.msg}`).join(", ")
          : "Could not save product";
      toast.error(msg);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast("Deleted");
    load();
  };

  const submitChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("New password and confirm don't match");
      return;
    }
    setPwLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password updated");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      setPwOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update password");
    } finally {
      setPwLoading(false);
    }
  };

  const regenerateKey = async () => {
    if (!window.confirm("This will invalidate your existing recovery key. Continue?")) return;
    try {
      const { data } = await api.post("/auth/regenerate-recovery-key");
      setRevealedKey(data.recovery_key);
    } catch {
      toast.error("Could not regenerate key");
    }
  };

  const resetUserPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? A temporary password will be generated.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`, {});
      setResetResult(data);
    } catch {
      toast.error("Could not reset");
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16 min-h-[80vh]" data-testid="admin-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-3">Atelier</div>
          <h1 className="font-serif-h font-light text-5xl text-stone-900">Admin</h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button onClick={regenerateKey} className="btn-gold-outline" data-testid="regen-key-btn">
            <ShieldCheck size={14} /> New Recovery Key
          </button>
          <Dialog open={pwOpen} onOpenChange={setPwOpen}>
            <DialogTrigger asChild>
              <button className="btn-gold-outline" data-testid="change-password-btn">
                <KeyRound size={14} /> Change Password
              </button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif-h text-2xl">Change Admin Password</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitChangePassword} className="space-y-6 pt-2" data-testid="change-password-form">
              <div>
                <label className="text-[11px] tracking-overline uppercase text-stone-500">Current Password</label>
                <input required type="password" className="input-line" value={pwForm.current_password}
                       onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                       data-testid="current-password" />
              </div>
              <div>
                <label className="text-[11px] tracking-overline uppercase text-stone-500">New Password (min 6 chars)</label>
                <input required type="password" minLength={6} className="input-line" value={pwForm.new_password}
                       onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                       data-testid="new-password" />
              </div>
              <div>
                <label className="text-[11px] tracking-overline uppercase text-stone-500">Confirm New Password</label>
                <input required type="password" minLength={6} className="input-line" value={pwForm.confirm}
                       onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                       data-testid="confirm-password" />
              </div>
              <DialogFooter>
                <button type="button" onClick={() => setPwOpen(false)} className="btn-gold-outline">Cancel</button>
                <button type="submit" disabled={pwLoading} className="btn-ink disabled:opacity-50" data-testid="submit-password">
                  {pwLoading ? "Saving..." : "Update Password"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <section className="mt-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif-h text-2xl">Products ({products.length})</h2>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button onClick={openCreate} className="btn-ink" data-testid="admin-new-btn"><Plus size={14}/> New Product</button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif-h text-2xl">{editing ? "Edit Product" : "New Product"}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                <div><label className="text-[11px] tracking-overline uppercase text-stone-500">Name <span className="text-[#5A1618]">*</span></label>
                  <input className="input-line" placeholder="e.g. Royal Magenta Silk Saree" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="adm-name"/></div>
                <div><label className="text-[11px] tracking-overline uppercase text-stone-500">Category</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sarees">Sarees</SelectItem><SelectItem value="lehengas">Party Wear</SelectItem>
                      <SelectItem value="kurtis">Kurtis</SelectItem><SelectItem value="dresses">Dresses</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><label className="text-[11px] tracking-overline uppercase text-stone-500">Price (₹)</label>
                  <input type="number" className="input-line" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="adm-price"/></div>
                <div><label className="text-[11px] tracking-overline uppercase text-stone-500">Compare at (optional)</label>
                  <input type="number" className="input-line" value={form.compare_at_price ?? ""} onChange={(e) => setForm({ ...form, compare_at_price: e.target.value || null })}/></div>
                <div><label className="text-[11px] tracking-overline uppercase text-stone-500">Stock</label>
                  <input type="number" className="input-line" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })}/></div>
                <div><label className="text-[11px] tracking-overline uppercase text-stone-500">Sizes (comma separated)</label>
                  <input className="input-line" value={form.sizes.join(",")} onChange={(e) => setForm({ ...form, sizes: e.target.value.split(",").map((s) => s.trim()) })}/></div>

                {/* Featured toggles — auto-unselect any other product currently holding that slot. */}
                <div className="md:col-span-2 border border-[#D4AF37]/30 bg-[#FDFBF7] p-4 rounded-sm space-y-3" data-testid="adm-featured-toggles">
                  <div className="text-[11px] tracking-overline uppercase text-[#D4AF37]">Spotlight</div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.featured_in_category}
                      onChange={(e) => setForm({ ...form, featured_in_category: e.target.checked })}
                      className="mt-1 accent-[#D4AF37]"
                      data-testid="adm-featured-category"
                    />
                    <div>
                      <div className="text-sm font-medium text-stone-900">⭐ Featured in category hero</div>
                      <div className="text-xs text-stone-500 mt-0.5">Shows as the big banner at the top of /shop/{form.category}. Only one product per category can hold this slot — others auto-unset.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.featured_on_homepage}
                      onChange={(e) => setForm({ ...form, featured_on_homepage: e.target.checked })}
                      className="mt-1 accent-[#D4AF37]"
                      data-testid="adm-featured-homepage"
                    />
                    <div>
                      <div className="text-sm font-medium text-stone-900">🌟 Featured on homepage</div>
                      <div className="text-xs text-stone-500 mt-0.5">The single site-wide hero on the homepage. Only one product can hold this slot at a time.</div>
                    </div>
                  </label>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[11px] tracking-overline uppercase text-stone-500">
                    Product Images <span className="text-[#5A1618]">*</span>
                  </label>

                  {/* Thumbnail grid */}
                  {visibleImages.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3" data-testid="adm-image-thumbs">
                      {visibleImages.map((url) => (
                        <div key={url} className="relative w-24 h-24 border border-warm rounded-sm overflow-hidden group">
                          <img
                            src={url.startsWith("http") ? url : `${window.location.origin}${url}`}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(url)}
                            data-testid={`adm-remove-img-${url}`}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-[#1C1917]/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                            aria-label="Remove image"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload dropzone */}
                  <label
                    htmlFor="adm-file-input"
                    className="mt-3 flex items-center gap-3 px-4 py-3 border border-dashed border-warm hover:border-[#D4AF37] cursor-pointer transition rounded-sm bg-[#FDFBF7]"
                    data-testid="adm-upload-dropzone"
                  >
                    <Upload size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
                    <span className="text-sm text-stone-700">
                      {uploading ? "Uploading…" : "Click to upload images (or drag-drop)"}
                    </span>
                    <span className="ml-auto text-[10px] tracking-overline uppercase text-stone-400">
                      JPG · PNG · WEBP · max 5 MB
                    </span>
                  </label>
                  <input
                    id="adm-file-input"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    data-testid="adm-file-input"
                    onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
                  />

                  {/* External URL fallback */}
                  <details className="mt-3">
                    <summary className="text-[11px] tracking-overline uppercase text-stone-500 cursor-pointer hover:text-[#D4AF37]">
                      Or paste image URLs
                    </summary>
                    <input
                      className="input-line mt-2"
                      placeholder="https://… , https://…"
                      value={form.images.join(",")}
                      onChange={(e) => setForm({ ...form, images: e.target.value.split(",").map((s) => s.trim()) })}
                      data-testid="adm-images"
                    />
                  </details>
                </div>
                <div className="md:col-span-2"><label className="text-[11px] tracking-overline uppercase text-stone-500">Tags (new, bestseller)</label>
                  <input className="input-line" value={(form.tags || []).join(",")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}/></div>
                <div className="md:col-span-2"><label className="text-[11px] tracking-overline uppercase text-stone-500">Description <span className="text-[#5A1618]">*</span></label>
                  <textarea className="input-line min-h-[100px]" placeholder="A short description that appears on the product page…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="adm-desc"/></div>
              </div>
              <DialogFooter>
                <button onClick={() => setOpen(false)} className="btn-gold-outline">Cancel</button>
                <button onClick={save} className="btn-ink" data-testid="adm-save">{editing ? "Save Changes" : "Create"}</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="border border-warm bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8EDE9] text-left text-[11px] tracking-overline uppercase text-stone-600">
              <tr>
                <th className="p-4">Item</th><th className="p-4">Category</th><th className="p-4">Price</th><th className="p-4">Stock</th><th className="p-4">Tags</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-warm" data-testid={`adm-row-${p.id}`}>
                  <td className="p-4 flex items-center gap-3">
                    <img src={p.images?.[0]} className="w-10 h-12 object-cover bg-[#F3EAE8]" alt=""/>
                    <span className="font-medium">{p.name}</span>
                  </td>
                  <td className="p-4 capitalize">{p.category}</td>
                  <td className="p-4">{rupee(p.price)}</td>
                  <td className="p-4">{p.stock}</td>
                  <td className="p-4 text-xs">{(p.tags || []).join(", ")}</td>
                  <td className="p-4 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(p)} className="p-2 hover:text-[#D4AF37]" data-testid={`adm-edit-${p.id}`}><Pencil size={14}/></button>
                    <button onClick={() => remove(p.id)} className="p-2 hover:text-red-500" data-testid={`adm-delete-${p.id}`}><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <CollectionsManager />

      <SiteSettingsManager />

      <StockAlertsManager />

      <section className="mt-20" data-testid="users-section">
        <h2 className="font-serif-h text-2xl mb-6">Customers ({users.length})</h2>
        {users.length === 0 ? (
          <div className="text-stone-500 text-sm">No customers yet.</div>
        ) : (
          <div className="border border-warm bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8EDE9] text-left text-[11px] tracking-overline uppercase text-stone-600">
                <tr>
                  <th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Joined</th><th className="p-4 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-warm" data-testid={`user-row-${u.id}`}>
                    <td className="p-4 font-medium">{u.name}</td>
                    <td className="p-4 text-stone-600">{u.email}</td>
                    <td className="p-4 text-xs uppercase tracking-wide text-stone-500">{u.role}</td>
                    <td className="p-4 text-xs text-stone-500">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="p-4 text-right">
                      {u.role !== "admin" && (
                        <button onClick={() => resetUserPassword(u)}
                                className="text-[11px] tracking-overline uppercase text-[#D4AF37] hover:text-[#B3932F]"
                                data-testid={`reset-user-${u.id}`}>
                          <RefreshCw size={12} className="inline mr-1" /> Reset Password
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mt-20">
        <h2 className="font-serif-h text-2xl mb-6">Orders ({orders.length})</h2>
        {orders.length === 0 ? (
          <div className="text-stone-500 text-sm">No orders yet.</div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.id} className="border border-warm bg-white p-5 flex flex-wrap justify-between gap-4 text-sm">
                <div>
                  <div className="font-medium">#{o.id.slice(0,8).toUpperCase()}</div>
                  <div className="text-xs text-stone-500">{o.shipping_address.full_name} · {o.shipping_address.city}, {o.shipping_address.state}</div>
                </div>
                <div className="text-xs text-stone-500">{new Date(o.created_at).toLocaleString()}</div>
                <div>{o.items.length} items</div>
                <div className="font-medium">{rupee(o.total)}</div>
                <div className="text-[11px] tracking-overline uppercase text-[#D4AF37]">{o.status}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Reveal recovery key dialog */}
      <Dialog open={!!revealedKey} onOpenChange={() => setRevealedKey(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif-h text-2xl">Save Your Recovery Key</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600">
            Store this key somewhere safe (password manager, encrypted note). It's the only way to recover your password if you ever forget it. <b>We can't show it again.</b>
          </p>
          <div className="border border-warm bg-[#F8EDE9]/40 p-4 rounded-sm">
            <div className="text-[10px] tracking-overline uppercase text-stone-500 mb-2">Recovery Key</div>
            <div className="flex items-center gap-3">
              <code className="text-sm break-all flex-1 font-mono">{revealedKey}</code>
              <button onClick={() => { navigator.clipboard.writeText(revealedKey); toast("Copied"); }}
                      className="p-2 hover:text-[#D4AF37]" data-testid="copy-recovery-key">
                <Copy size={16} />
              </button>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setRevealedKey(null)} className="btn-ink">I've Saved It</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset user result dialog */}
      <Dialog open={!!resetResult} onOpenChange={() => setResetResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif-h text-2xl">Customer Password Reset</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600">
            Send this temporary password to the customer. They should change it after signing in.
          </p>
          {resetResult && (
            <div className="border border-warm bg-[#F8EDE9]/40 p-4 rounded-sm">
              <div className="text-[10px] tracking-overline uppercase text-stone-500">{resetResult.email}</div>
              <div className="flex items-center gap-3 mt-2">
                <code className="text-sm break-all flex-1 font-mono">{resetResult.new_password}</code>
                <button onClick={() => { navigator.clipboard.writeText(resetResult.new_password); toast("Copied"); }}
                        className="p-2 hover:text-[#D4AF37]" data-testid="copy-temp-password">
                  <Copy size={16} />
                </button>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setResetResult(null)} className="btn-ink">Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
